import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, CalendarDays, CalendarOff, AlertTriangle, Briefcase, MapPinned, Repeat, Clock } from 'lucide-react';
import api from '../utils/api';

const STATUS_META = {
  available:       { label: 'Available',        icon: CalendarDays, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  unavailable:     { label: 'Unavailable',      icon: CalendarOff,  cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  tentative:       { label: 'Tentative',        icon: AlertTriangle, cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  booked_external: { label: 'Booked elsewhere', icon: Briefcase,    cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  out_of_market:   { label: 'Out of market',    icon: MapPinned,    cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function Pill({ status }) {
  const m = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-700 border-gray-200', icon: CalendarDays };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-semibold border ${m.cls}`}>
      <Icon className="w-3 h-3" />{m.label}
    </span>
  );
}

/**
 * Iter-226g — Read-only admin view of a talent's availability blocks
 * + weekly recurring rules. Used inside the admin User Detail page.
 */
export default function TalentAvailabilityCard({ talentId }) {
  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    if (!talentId) return;
    const today = new Date().toISOString().slice(0, 10);
    api.get(`/admin/talent/${talentId}/availability`, { params: { from_date: today } })
      .then(r => { setBlocks(r.data.blocks || []); setRules(r.data.recurring || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [talentId]);

  return (
    <Card data-testid="admin-talent-availability-card">
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
          <CalendarDays className="w-4 h-4 text-cyan-600" /> Talent Availability
        </h3>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
        ) : (blocks.length === 0 && rules.length === 0) ? (
          <p className="text-xs text-gray-500 text-center py-3" data-testid="admin-talent-availability-empty">
            No blackout dates or recurring restrictions on file.
          </p>
        ) : (
          <div className="space-y-3">
            {blocks.length > 0 && (
              <div data-testid="admin-talent-availability-blocks">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                  Upcoming blocks ({blocks.length})
                </p>
                <div className="space-y-1">
                  {blocks.slice(0, 8).map(b => (
                    <div key={b.id} className="text-xs flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800">
                        {b.start_date}{b.start_date !== b.end_date && ` → ${b.end_date}`}
                      </span>
                      <Pill status={b.status} />
                      {b.start_time && b.end_time && (
                        <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-gray-100 text-gray-600 border-0">
                          <Clock className="w-3 h-3 mr-0.5" />{b.start_time}–{b.end_time}
                        </Badge>
                      )}
                      {b.reason && <span className="text-[11px] text-gray-500 italic truncate">— {b.reason}</span>}
                    </div>
                  ))}
                  {blocks.length > 8 && <p className="text-[10px] text-gray-400">+{blocks.length - 8} more</p>}
                </div>
              </div>
            )}
            {rules.length > 0 && (
              <div data-testid="admin-talent-availability-rules">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 flex items-center gap-1">
                  <Repeat className="w-3 h-3" /> Recurring schedule
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {rules.map(r => (
                    <div key={r.id} className="text-[11px] border border-gray-100 rounded px-2 py-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-gray-700">{DAY_NAMES[r.day_of_week]}</span>
                      <Pill status={r.status} />
                      {r.start_time && r.end_time && (
                        <span className="text-gray-500">{r.start_time}–{r.end_time}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
